'use client';

import * as React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ViewContainer from '../layouts/ViewContainer';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTheme } from '@mui/material/styles';

interface Transaction {
    id: number;
    amount: number;
    type: string;
    description: string | null;
    createdAt: string;
}

interface TransactionsViewProps {
    onBack: () => void;
}

export default function TransactionsView({ onBack }: TransactionsViewProps) {
  const [transactions, setTransactions] = React.useState<Transaction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const theme = useTheme();

  React.useEffect(() => {
      const fetchTransactions = async () => {
          try {
              const response = await fetch('/transactions');
              const data = await response.json();
              
              if (data.success) {
                  setTransactions(data.transactions);
              } else {
                  setError(data.error || 'Не удалось загрузить историю');
              }
          } catch (e) {
              setError('Ошибка сети');
          } finally {
              setLoading(false);
          }
      };
      
      fetchTransactions();
  }, []);

  const getTransactionIcon = (amount: number) => {
      if (amount > 0) {
          return <AddIcon sx={{ color: 'success.main' }} />;
      }
      return <RemoveIcon sx={{ color: 'error.main' }} />;
  };

  const getTransactionTitle = (type: string, description: string | null) => {
      if (description) return description;
      switch (type) {
          case 'deposit': return 'Пополнение баланса';
          case 'device_activation': return 'Активация устройства';
          case 'daily_rent': return 'Аренда устройства';
          default: return 'Операция';
      }
  };

  return (
    <ViewContainer title="История операций" onClose={onBack}>
      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
          </Box>
      ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
      ) : transactions.length === 0 ? (
          <Typography align="center" color="text.secondary">История операций пуста</Typography>
      ) : (
          <Stack spacing={2}>
              {transactions.map((tx) => (
                  <Paper
                      key={tx.id}
                      elevation={0}
                      sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          gap: 2 // Отступ между левой частью и суммой
                      }}
                  >
                      <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          flexGrow: 1, // Занимает все доступное место
                          minWidth: 0, // Позволяет тексту сжиматься/переноситься
                      }}>
                          <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              flexShrink: 0, // Иконка не сжимается
                              backgroundColor: tx.amount > 0 
                                ? theme.palette.success.main + '1A'
                                : theme.palette.error.main + '1A',
                          }}>
                              {getTransactionIcon(tx.amount)}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                              <Typography 
                                variant="body2" // Уменьшил размер шрифта
                                fontWeight="bold" 
                                noWrap // Обрезает текст с троеточием, если не влезает
                                title={getTransactionTitle(tx.type, tx.description) || ''} // Тултип при наведении
                              >
                                  {getTransactionTitle(tx.type, tx.description)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                  {new Date(tx.createdAt).toLocaleString()}
                              </Typography>
                          </Box>
                      </Box>
                      
                      <Typography 
                        variant="body1" 
                        fontWeight="bold" 
                        color={'text.primary'} // Убрал зеленый цвет
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} // Сумма не сжимается и не переносится
                      >
                          {tx.amount > 0 ? '+' : ''}{tx.amount} ₽
                      </Typography>
                  </Paper>
              ))}
          </Stack>
      )}
    </ViewContainer>
  );
}
